/**
 * "Generate the integration kit" — CLI section (ADR-0440).
 * Reconciled from packages/cli/src/commands/generate + commands/integrate.
 */
import type { DocPageContent } from '../types';

export const CLI_GENERATE_PAGE: DocPageContent = {
  slug: 'cli/generate',
  titleKey: 'docs.cli.generate.title',
  descriptionKey: 'docs.cli.generate.description',
  blocks: [
    { kind: 'paragraph', textKey: 'docs.cli.generate.lead' },
    { kind: 'code', language: 'bash', code: 'npx syncanix generate --key pk_live_…' },

    { kind: 'heading', level: 2, id: 'what-you-get', textKey: 'docs.cli.generate.files.heading' },
    { kind: 'paragraph', textKey: 'docs.cli.generate.files.body' },
    {
      kind: 'code',
      language: 'text',
      code: [
        'syncanix/',
        '  index.ts         the one file you import — installs the widget, applies everything below',
        '  widget.ts        installs the widget from code (idempotent, with a teardown)',
        '  types.d.ts       types for window.syncanix, so your editor knows the API',
        '  actions.ts       functions the agent may run on the page',
        '  components.ts    UI the agent may render in the chat',
        '  data-sources.ts  live data the agent may read',
        '  page-actions.ts  the routes and controls from your last scan',
        '  csp.ts           the CSP sources the widget needs, as a value to spread',
        '  identity.ts      your token provider (written only with --idp)',
      ].join('\n'),
    },
    { kind: 'paragraph', textKey: 'docs.cli.generate.files.barrel' },
    { kind: 'code', language: 'ts', code: "import './syncanix';" },

    { kind: 'heading', level: 2, id: 'editing', textKey: 'docs.cli.generate.editing.heading' },
    { kind: 'paragraph', textKey: 'docs.cli.generate.editing.body' },
    {
      kind: 'callout',
      tone: 'tip',
      textKey: 'docs.cli.generate.editing.force',
    },

    { kind: 'heading', level: 2, id: 'identity', textKey: 'docs.cli.generate.identity.heading' },
    { kind: 'paragraph', textKey: 'docs.cli.generate.identity.body' },
    {
      kind: 'code',
      language: 'bash',
      code: 'npx syncanix generate --key pk_live_… --idp auth0',
    },
    {
      kind: 'code',
      language: 'ts',
      code: [
        "import { connectSyncanixIdentity } from './syncanix/identity';",
        '',
        'connectSyncanixIdentity(() => auth0.getAccessTokenSilently());',
      ].join('\n'),
    },

    { kind: 'heading', level: 2, id: 'page-actions', textKey: 'docs.cli.generate.scan.heading' },
    { kind: 'paragraph', textKey: 'docs.cli.generate.scan.body' },
    { kind: 'code', language: 'bash', code: 'npx syncanix init\nnpx syncanix generate' },

    { kind: 'heading', level: 2, id: 'or-integrate', textKey: 'docs.cli.generate.compare.heading' },
    { kind: 'paragraph', textKey: 'docs.cli.generate.compare.body' },
    {
      kind: 'definitions',
      variant: 'code',
      items: [
        {
          termKey: 'docs.cli.generate.compare.integrate.term',
          descKey: 'docs.cli.generate.compare.integrate.desc',
        },
        {
          termKey: 'docs.cli.generate.compare.generate.term',
          descKey: 'docs.cli.generate.compare.generate.desc',
        },
        {
          termKey: 'docs.cli.generate.compare.both.term',
          descKey: 'docs.cli.generate.compare.both.desc',
        },
      ],
    },
    { kind: 'code', language: 'bash', code: 'npx syncanix integrate --kit' },
    {
      kind: 'callout',
      tone: 'note',
      textKey: 'docs.cli.generate.compare.implied',
    },

    { kind: 'heading', level: 2, id: 'next', textKey: 'docs.common.nextSteps' },
    {
      kind: 'cardGrid',
      cards: [
        {
          titleKey: 'docs.cli.generate.next.console.title',
          textKey: 'docs.cli.generate.next.console.text',
          to: { docSlug: 'cli/console' },
        },
        {
          titleKey: 'docs.cli.generate.next.widget.title',
          textKey: 'docs.cli.generate.next.widget.text',
          to: { docSlug: 'widget' },
        },
      ],
    },
  ],
};
